"use client";

import { ChevronRight, File, FileCode, FileText, Folder, MoreHorizontal } from "lucide-react";

import { buildSkillTree, type SkillTreeNode } from "@/components/command-skills/skill-utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import type { SkillFile } from "@/types/command-skill";

interface SkillFileTreeProps {
  files: SkillFile[];
  selectedPath: string;
  canEdit: boolean;
  collapsed: Record<string, boolean>;
  onToggleFolder: (path: string) => void;
  onSelect: (path: string) => void;
  onAddFile?: () => void;
  onReplace: (path: string) => void;
  onDelete: (path: string) => void;
}

export function SkillFileTree({
  files,
  selectedPath,
  canEdit,
  collapsed,
  onToggleFolder,
  onSelect,
  onAddFile,
  onReplace,
  onDelete,
}: SkillFileTreeProps) {
  const tree = buildSkillTree(files);

  return (
    <div className="flex min-h-0 flex-col p-3">
      <div className="mb-1 flex items-center justify-between gap-2 px-2">
        <h2 className="text-[13px] font-medium text-foreground/80">Files</h2>
        {canEdit ? (
          <button
            type="button"
            onClick={onAddFile}
            className="text-[13px] font-medium text-muted-foreground hover:text-foreground"
          >
            + Add file
          </button>
        ) : null}
      </div>
      <div className="space-y-0.5">
        {tree.map((node) => (
          <TreeNode
            key={node.path}
            node={node}
            depth={0}
            selectedPath={selectedPath}
            canEdit={canEdit}
            collapsed={collapsed}
            onToggleFolder={onToggleFolder}
            onSelect={onSelect}
            onReplace={onReplace}
            onDelete={onDelete}
          />
        ))}
      </div>
    </div>
  );
}

function TreeNode({
  node,
  depth,
  selectedPath,
  canEdit,
  collapsed,
  onToggleFolder,
  onSelect,
  onReplace,
  onDelete,
}: {
  node: SkillTreeNode;
  depth: number;
  selectedPath: string;
  canEdit: boolean;
  collapsed: Record<string, boolean>;
  onToggleFolder: (path: string) => void;
  onSelect: (path: string) => void;
  onReplace: (path: string) => void;
  onDelete: (path: string) => void;
}) {
  if (node.type === "folder") {
    const isOpen = !collapsed[node.path];
    return (
      <div>
        <button
          type="button"
          className="flex w-full items-center gap-1.5 rounded-lg px-2 py-1.5 text-left text-sm hover:bg-muted"
          style={{ paddingLeft: 8 + depth * 14 }}
          onClick={() => onToggleFolder(node.path)}
        >
          <ChevronRight className={cn("size-3.5 shrink-0 text-muted-foreground transition-transform", isOpen && "rotate-90")} />
          <Folder className="size-3.5 shrink-0 text-muted-foreground" />
          <span className="truncate">{node.name}</span>
        </button>
        {isOpen
          ? node.children.map((child) => (
              <TreeNode
                key={child.path}
                node={child}
                depth={depth + 1}
                selectedPath={selectedPath}
                canEdit={canEdit}
                collapsed={collapsed}
                onToggleFolder={onToggleFolder}
                onSelect={onSelect}
                onReplace={onReplace}
                onDelete={onDelete}
              />
            ))
          : null}
      </div>
    );
  }

  const Icon = fileIcon(node.path);
  const showMenu = canEdit && node.path !== "SKILL.md";

  return (
    <div
      className={cn(
        "group flex items-center gap-1 rounded-lg pr-1",
        selectedPath === node.path ? "bg-muted" : "hover:bg-muted/70",
      )}
      style={{ paddingLeft: 8 + depth * 14 }}
    >
      <button
        type="button"
        className="flex min-w-0 flex-1 items-center gap-1.5 py-1.5 text-left text-sm"
        onClick={() => onSelect(node.path)}
      >
        <Icon className="size-3.5 shrink-0 text-muted-foreground" />
        <span className="truncate">{node.name}</span>
      </button>
      {showMenu ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="rounded-md p-1 text-muted-foreground hover:bg-background focus-visible:opacity-100 group-hover:opacity-100 data-[state=open]:opacity-100 max-sm:opacity-100 sm:opacity-0"
              aria-label={`File actions for ${node.name}`}
            >
              <MoreHorizontal className="size-3.5" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onReplace(node.path)}>Replace</DropdownMenuItem>
            <DropdownMenuItem
              className="text-destructive focus:text-destructive"
              onClick={() => onDelete(node.path)}
            >
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
    </div>
  );
}

function fileIcon(path: string) {
  if (path === "SKILL.md" || path.toLowerCase().endsWith(".md")) return FileText;
  if (/\.(py|js|jsx|ts|tsx|sh|json|ya?ml|csv|css|sql)$/i.test(path)) return FileCode;
  return File;
}
